import React, { useState } from "react";
import verifiedtick from "../../../images/verifiedtick.svg";
import {
  Button,
  Dropdown,
  Flex,
  Menu,
  MenuProps,
  message,
  Modal,
  Popover,
  Progress,
  Tag,
  Tooltip,
  notification,
} from "antd";
import { SvgIcons } from "Icon/SvgIcons";
import { addCandidateToRole } from "api/project.api";
import npiIcon from "../../../images/npi.png";
import { useAppDispatch } from "hooks/reduxHooks";
import { toggleProjectDetailRefresh } from "store/slices/talentPool";
import ProfileLogo from "../../../images/download.png";
import CandidateNotesModal from "components/AddCandidateNote";
import CandidateModal from "components/Common/Candidate Actions/AddToTalentPool";
import { useFeatureAllowedFromState } from "services/CustomeHooks";
import { FEATURE_CONSTANTS } from "components/constants/AppConstants";
import SendBulkEmail from "components/CandidateProfile/SendBulkEmail";
import { replaceReduxCandidates } from "store/slices/candidateBulkMail.slice";
import {
  DoximityIconCompoGray,
  GreenhouseSVG,
  HubspotIconCompoGray,
  HubspotIconCompoOrange,
  LeverIconCompoGreen,
  SmartRecruiterIcon,
} from "Icon/Icon";
import {
  CrownFilled,
  EyeOutlined,
  IdcardOutlined,
  MailOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import { GreenhouseErrorSVG, SmartRecruiterIconError } from "icons/Icons";
import { useNavigate } from "react-router-dom";
import { setRejectedCandidate } from "store/slices/refresh.slice";
import { readUser } from "services/localStorage.service";
import TooltipWrapper from "components/CandidateProfile/TooltipWrapper";
import { isLinkedInOrDoximity } from "utils/utils";
import ShortlistCandidate from "../steps/Step4Candidates/ShortlistCandidate";
import RejectCandidate from "../steps/Step4Candidates/RejectCandidate";

interface TagsIO {
  ADDED_TO_PROJECT?: any[];
  VIEWED?: any[];
}

export const icons: any = {
  ADDED_TO_TALENT_POOL: <CrownFilled />,
  CONTACTED: <MailOutlined />,
  VIEWED: <EyeOutlined />,
  LICENSE: <IdcardOutlined />,
  SMART_RECRUITER: <SmartRecruiterIcon />,
  SMART_RECRUITER_ERROR: <SmartRecruiterIconError />,
  GREENHOUSE: <GreenhouseSVG />,
  GREENHOUSE_ERROR: <GreenhouseErrorSVG />,
  ADDED_TO_PROJECT: <UserAddOutlined />,
  SENT_TO_LEVER: <LeverIconCompoGreen />,
  SENT_TO_HUBSPOT: <HubspotIconCompoOrange />,
  SENT_TO_HUBSPOT_ERROR: <HubspotIconCompoGray />,
  LINKEDIN_ICON: SvgIcons.linkedinSvg,
  DOXIMITY_ICON: <DoximityIconCompoGray />,
};

interface ICandidateBasicDetails {
  name: string;
  location: string;
  score: number | null;
  profileImg: string;
  linkedin_url?: string;
  mail?: string;
  showDrawer?: any;
  listViewType?: string;
  shortList?: boolean;
  candidateId: number;
  selectedPool?: number | null;
  tags: TagsIO;
  selectedCandidates: number[];
  licenses: any[];
  npi: number | null;
  isSearch?: boolean;
  isRejectedCandidate?: boolean;
  isContactInfoNotAllowed?: boolean;
  candidateDetails: any;
  tooltips?: any;
  eyeIconContent?: any;
  projectIconContent?: any;
  searchId?: any;
  handleShortlistCandidate?: any;
  handleRejectCandidate?: any;
}

const CandidateBasicDetails: React.FC<ICandidateBasicDetails> = ({
  name,
  location,
  score,
  profileImg,
  linkedin_url,
  mail,
  showDrawer,
  shortList = false,
  candidateId,
  selectedPool,
  tags,
  licenses,
  npi,
  candidateDetails,
  isRejectedCandidate,
  isContactInfoNotAllowed,
  tooltips,
  eyeIconContent,
  projectIconContent,
  searchId,
  handleShortlistCandidate,
  handleRejectCandidate,
}) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const navigate = useNavigate();
  const getUserInfo = readUser();

  const getATSTitle = (key: string) => {
    if (key === "SENT_TO_SMARTRECRUITERS") {
      return "Smart Recruiters";
    } else if (key === "SENT_TO_GREENHOUSE") {
      return "Greenhouse";
    } else if (key === "SENT_TO_LEVER") {
      return "Lever";
    } else if (key === "SENT_TO_HUBSPOT") {
      return "Hubspot";
    }
  };

  const getContentKey = (key: string) => {
    if (key === "SENT_TO_HUBSPOT") {
      return "STATUS";
    } else {
      return "STATUS";
    }
  };

  const handleShortList = async () => {
    setLoading(true);
    try {
      const response = await addCandidateToRole({
        jobs: [selectedPool],
        candidates: [candidateId],
        is_shortlisted: true,
      });
      dispatch(setRejectedCandidate([candidateId]));

      dispatch(toggleProjectDetailRefresh());
      // message.success("Candidate successfully shortlisted!");
      notification.success({
        message: "Success",
        description: "Candidate successfully shortlisted!",
      });
    } catch (error) {
      message.error(`Failed to shortlist candidate: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  const bulkEmailCandidateTrigger = async (e: any) => {
    e.preventDefault();
    dispatch(
      replaceReduxCandidates([
        {
          id: candidateDetails.id,
          name: candidateDetails.full_name,
          email: candidateDetails.email,
          profile: candidateDetails.profile_image_url,
          location: candidateDetails.location,
          jobTitle: candidateDetails.titles,
          graphProfileId: candidateDetails.graph_profile_id,
        },
      ])
    );
  };

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const getATSIcon = (key: string) => {
    if (key === "SENT_TO_SMARTRECRUITERS") {
      return candidateDetails.tags[key] ? icons["SMART_RECRUITER"] : icons["SMART_RECRUITER_ERROR"];
    } else if (key === "SENT_TO_GREENHOUSE") {
      return candidateDetails.tags[key] ? icons["GREENHOUSE"] : icons["GREENHOUSE_ERROR"];
    } else if (key === "SENT_TO_LEVER") {
      return candidateDetails.tags[key] ? icons["SENT_TO_LEVER"] : icons["SENT_TO_LEVER_ERROR"];
    } else if (key === "SENT_TO_HUBSPOT") {
      return candidateDetails.tags[key] ? icons["SENT_TO_HUBSPOT"] : icons["SENT_TO_HUBSPOT_ERROR"];
    }
  };

  const getATSLinkKey = (key: string) => {
    if (key === "SENT_TO_SMARTRECRUITERS") {
      return "SMARTRECRUITER_URL";
    } else if (key === "SENT_TO_GREENHOUSE") {
      return "GREENHOUSE_URL";
    } else if (key === "SENT_TO_LEVER") {
      return "LEVER_URL";
    } else if (key === "SENT_TO_HUBSPOT") {
      return "HUBSPOT_URL";
    } else {
      return "";
    }
  };

  const getProfileIcon = (url: string) => {
    const domain = isLinkedInOrDoximity(url);
    if (domain === "linkedin") {
      return icons["LINKEDIN_ICON"];
    } else if (domain === "doximity") {
      return icons["DOXIMITY_ICON"];
    }
  };

  const isFeatureAllowed = useFeatureAllowedFromState(FEATURE_CONSTANTS.FCPV);

  const emailOptions =
    candidateDetails?.emails?.map((email: string) => ({ value: email, label: email })) || [];

  return (
    <div className="flex flex-1 justify-center">
      <div className="w-full">
        <div className="rounded-full w-28 h-28 mx-auto border border-1 border-secondary-border relative cursor-pointer">
          <img
            src={profileImg ? profileImg : ProfileLogo}
            onClick={showDrawer}
            className="rounded-full cursor-pointer object-cover object-center  w-28 h-28"
          />
          {licenses?.length > 0 &&
            licenses.length &&
            licenses.some((license: any) => license.verified) && (
              <div className="absolute right-[-10px] top-1/2 w-5 h-5 transform rotate-10">
                {SvgIcons.verifiedSvg}
              </div>
            )}
          {npi && (
            <a
              title="NPI Registry"
              style={{ color: "#1890ff", textDecoration: "none" }}
              href={`https://npiregistry.cms.hhs.gov/provider-view/${npi}`}
              target={"_blank"}
              rel="noreferrer"
            >
              <img
                className="absolute right-[-10px] top-1/2 w-5 h-5 transform rotate-10"
                src={npiIcon}
              />
            </a>
          )}
        </div>

        <Flex vertical gap={10}>
          <div className="mt-4 text-center">
            <TooltipWrapper text={tooltips?.name} placement="top">
              <p
                onClick={showDrawer}
                className="text-fonttext-primary font-medium text-sm cursor-pointer hover:text-gray-500 px-2 rounded-md"
              >
                {name}
              </p>
            </TooltipWrapper>
            <div>
              <TooltipWrapper text={tooltips?.location} placement="right">
                <p className="text-text-quaternary text-xs">{location}</p>
              </TooltipWrapper>
            </div>
          </div>
          {!isContactInfoNotAllowed && (
            <div className="flex  gap-2.5 mt-3 justify-center items-center">
              <TooltipWrapper text={tooltips?.location} placement="right">
                {linkedin_url && (
                  <a className="relative" href={linkedin_url} target="_blank" rel="noreferrer">
                    {getProfileIcon(linkedin_url)}
                  </a>
                )}
              </TooltipWrapper>

              {mail && getUserInfo.role !== "EXTERNAL_CONTRIBUTOR" && (
                <>
                  {isFeatureAllowed && emailOptions.length > 0 ? (
                    <SendBulkEmail
                      customButton={
                        <a
                          className="relative"
                          href={undefined}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => {
                            bulkEmailCandidateTrigger(e);
                          }}
                        >
                          {SvgIcons.mailBigSvg}
                          <img
                            src={verifiedtick}
                            className="absolute w-2.5 h-2.5 top-0 right-[-4px]"
                          />
                        </a>
                      }
                      profileData={candidateDetails}
                      autoGenerate={true}
                      emails={
                        candidateDetails && candidateDetails.emails ? candidateDetails.emails : []
                      }
                      profileId={candidateDetails?.graph_profile_id}
                      candidateId={candidateDetails}
                    ></SendBulkEmail>
                  ) : (
                    ""
                  )}
                </>
              )}
              {candidateDetails &&
                candidateDetails?.tags &&
                Object.keys(candidateDetails?.tags)
                  .filter((tag) => tag !== "ADDED_TO_TALENT_POOL")
                  .map((key) => {
                    return (
                      [
                        "SENT_TO_SMARTRECRUITERS",
                        "SENT_TO_GREENHOUSE",
                        "SENT_TO_LEVER",
                        "SENT_TO_HUBSPOT",
                      ].includes(key) && (
                        <Tooltip
                          title={
                            Array.isArray(candidateDetails?.tags[getContentKey(key)])
                              ? candidateDetails?.tags[getContentKey(key)]?.join(", ")
                              : candidateDetails?.tags[getContentKey(key)]
                          }
                        >
                          <Button
                            type="link"
                            icon={getATSIcon(key)}
                            title={getATSTitle(key)}
                            className="gray-gray-9"
                            onClick={() =>
                              candidateDetails?.tags[getATSLinkKey(key)] &&
                              window.open(candidateDetails?.tags[getATSLinkKey(key)])
                            }
                          ></Button>
                        </Tooltip>
                      )
                    );
                  })}
              <button className="relative" onClick={() => setIsModalOpen(true)}>
                <svg
                  width="15"
                  height="16"
                  viewBox="0 0 15 16"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M13.9219 8H12.9375C12.8602 8 12.7969 8.06328 12.7969 8.14062V13.7656H1.26562V2.23438H6.89062C6.96797 2.23438 7.03125 2.17109 7.03125 2.09375V1.10938C7.03125 1.03203 6.96797 0.96875 6.89062 0.96875H0.5625C0.251367 0.96875 0 1.22012 0 1.53125V14.4688C0 14.7799 0.251367 15.0312 0.5625 15.0312H13.5C13.8111 15.0312 14.0625 14.7799 14.0625 14.4688V8.14062C14.0625 8.06328 13.9992 8 13.9219 8Z"></path>
                  <path d="M4.28797 8.40254L4.25458 10.4926C4.25282 10.649 4.37938 10.7773 4.53583 10.7773H4.54286L6.61708 10.7264C6.65223 10.7246 6.68739 10.7105 6.712 10.6859L14.0227 3.39102C14.0772 3.33652 14.0772 3.24687 14.0227 3.19238L11.8378 1.00918C11.8097 0.981055 11.7745 0.96875 11.7376 0.96875C11.7007 0.96875 11.6655 0.982813 11.6374 1.00918L4.32841 8.3041C4.30302 8.33065 4.28858 8.36581 4.28797 8.40254ZM5.40419 8.81738L11.7376 2.49805L12.5321 3.29082L6.1952 9.61367L5.39188 9.63301L5.40419 8.81738Z"></path>
                </svg>
              </button>
            </div>
          )}
          {selectedPool && (
            <div className="mt-3 flex justify-center gap-2.5 btnRow">
              <ShortlistCandidate
                candidateId={candidateId}
                selectedPool={selectedPool}
                searchId={searchId}
                isSelected={candidateDetails?.is_shortlisted}
                handleShortlistCandidate={handleShortlistCandidate}
              />
              {selectedPool && (
                <Tooltip title="Reject Candidate">
                  <RejectCandidate
                    selectedPool={selectedPool}
                    selectedCandidates={candidateId}
                    searchId={searchId}
                    isSelected={candidateDetails?.is_rejected}
                    handleRejectCandidate={handleRejectCandidate}
                  />
                </Tooltip>
              )}
            </div>
          )}
          {/*  {score !== null && score > 0 && false ? (
            <div className="bg-utility-bluelight rounded-md flex justify-center items-center flex-col p-[6px]">
              <Flex className="text-xs gap-2 leading-6" align="center">
                {SvgIcons.orangeStarSvg} <span className="text-warning-secondary">4/5</span>
                <span>Readiness</span>
              </Flex>
              <div className="flex gap-2 justify-center items-center w-[154px] leading-6">
                <p className="text-[#2D3648] text-xs">Rank</p>
                <Progress
                  size="small"
                  percent={score}
                  type="line"
                  strokeColor="#2EDB4B"
                  trailColor="#d9d9d9"
                />
              </div>
            </div>
          ) : (
            ""
          )}*/}

          <div>
            {/* <div className="flex gap-2 mt-3 items-center">
              <p className="text-[#2D3648] text-xs">Rank</p>
              <Progress percent={score ?? 0} type="line" strokeColor="#2EDB4B" trailColor="#d9d9d9" />
            </div> */}
          </div>
          <div className="flex justify-center items-start gap-4 flex-shrink-0 self-stretch">
            {tags?.ADDED_TO_PROJECT && tags?.ADDED_TO_PROJECT?.length > 0 ? (
              <div>
                <Popover content={projectIconContent} title="Project View">
                  <Flex align="center" gap={4} className="cursor-pointer">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <path
                        d="M14.5 6.9375H12.8125V5.1625C12.8125 4.88594 12.5891 4.6625 12.3125 4.6625H7.39062L5.55781 2.90937C5.53449 2.88754 5.50382 2.87527 5.47188 2.875H1.5C1.22344 2.875 1 3.09844 1 3.375V12.625C1 12.9016 1.22344 13.125 1.5 13.125H12.4062C12.6094 13.125 12.7937 13.0016 12.8703 12.8125L14.9641 7.625C14.9875 7.56563 15 7.50156 15 7.4375C15 7.16094 14.7766 6.9375 14.5 6.9375ZM2.125 4H5.07031L6.93906 5.7875H11.6875V6.9375H3.71875C3.51562 6.9375 3.33125 7.06094 3.25469 7.25L2.125 10.05V4ZM12.0516 12H2.48438L4.09844 8H13.6672L12.0516 12Z"
                        fill="#40A9FF"
                      />
                    </svg>
                    <span className="text-xs text-gray-9">
                      In {tags?.ADDED_TO_PROJECT?.length} Projects
                    </span>
                  </Flex>
                </Popover>
              </div>
            ) : (
              ""
            )}
            {tags?.VIEWED?.length ? (
              <Popover content={eyeIconContent} title="Profile View">
                <Flex align="center" gap={4} className="cursor-pointer">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M14.722 7.59688C13.2407 4.47656 11.0017 2.90625 8.00009 2.90625C4.99697 2.90625 2.75947 4.47656 1.27822 7.59844C1.2188 7.72425 1.18799 7.86165 1.18799 8.00078C1.18799 8.13991 1.2188 8.27732 1.27822 8.40312C2.75947 11.5234 4.99853 13.0938 8.00009 13.0938C11.0032 13.0938 13.2407 11.5234 14.722 8.40156C14.8423 8.14844 14.8423 7.85469 14.722 7.59688ZM8.00009 11.9688C5.47978 11.9688 3.63447 10.6906 2.3329 8C3.63447 5.30938 5.47978 4.03125 8.00009 4.03125C10.5204 4.03125 12.3657 5.30938 13.6673 8C12.3673 10.6906 10.522 11.9688 8.00009 11.9688ZM7.93759 5.25C6.41884 5.25 5.18759 6.48125 5.18759 8C5.18759 9.51875 6.41884 10.75 7.93759 10.75C9.45634 10.75 10.6876 9.51875 10.6876 8C10.6876 6.48125 9.45634 5.25 7.93759 5.25ZM7.93759 9.75C6.9704 9.75 6.18759 8.96719 6.18759 8C6.18759 7.03281 6.9704 6.25 7.93759 6.25C8.90478 6.25 9.68759 7.03281 9.68759 8C9.68759 8.96719 8.90478 9.75 7.93759 9.75Z"
                      fill="#40A9FF"
                    />
                  </svg>
                  <span className="text-xs text-gray-9">{tags?.VIEWED?.length} Views</span>
                </Flex>
              </Popover>
            ) : (
              ""
            )}{" "}
          </div>

          {/* </>)} */}
        </Flex>
      </div>

      <CandidateNotesModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        candidateId={candidateId}
      />
      <CandidateModal
        isVisible={isModalVisible}
        onClose={handleCancel}
        selectedCandidates={[candidateId]}
        selectedPool={selectedPool}
        isAddToProject={true}
      />
    </div>
  );
};

export default CandidateBasicDetails;
